import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RouteVisualization = ({ selectedRoute, routes, onRouteSelect, isOptimizing }) => {
  const [mapView, setMapView] = useState('satellite');
  const [showWeatherOverlay, setShowWeatherOverlay] = useState(false);
  const [showEmissionsOverlay, setShowEmissionsOverlay] = useState(true);

  // Mock route data for visualization
  const mockRoutes = routes?.length > 0 ? routes : [
    {
      id: 'route-1',
      name: 'Direct Route',
      type: 'direct',
      distance: '4,850 nm',
      duration: '14.2 days',
      fuelConsumption: '1,245 MT',
      co2Emissions: '3,920 MT',
      euaCost: '$196,000',
      ciiImpact: 'B',
      waypoints: [
        { lat: 51.9225, lng: 4.4792, name: 'Rotterdam' },
        { lat: 1.2966, lng: 103.7764, name: 'Singapore' }
      ],
      segments: [
        { from: 'Rotterdam', to: 'Suez Canal', emissions: 'Medium', weather: 'Favorable' },
        { from: 'Suez Canal', to: 'Singapore', emissions: 'High', weather: 'Moderate' }
      ]
    },
    {
      id: 'route-2',
      name: 'Weather Optimized',
      type: 'weather',
      distance: '4,920 nm',
      duration: '13.8 days',
      fuelConsumption: '1,180 MT',
      co2Emissions: '3,715 MT',
      euaCost: '$185,750',
      ciiImpact: 'B+',
      waypoints: [
        { lat: 51.9225, lng: 4.4792, name: 'Rotterdam' },
        { lat: 1.2966, lng: 103.7764, name: 'Singapore' }
      ],
      segments: [
        { from: 'Rotterdam', to: 'Suez Canal', emissions: 'Low', weather: 'Optimal' },
        { from: 'Suez Canal', to: 'Singapore', emissions: 'Medium', weather: 'Good' }
      ]
    },
    {
      id: 'route-3',
      name: 'Emissions Minimized',
      type: 'emissions',
      distance: '5,100 nm',
      duration: '15.1 days',
      fuelConsumption: '1,050 MT',
      co2Emissions: '3,308 MT',
      euaCost: '$165,400',
      ciiImpact: 'A',
      waypoints: [
        { lat: 51.9225, lng: 4.4792, name: 'Rotterdam' },
        { lat: 1.2966, lng: 103.7764, name: 'Singapore' }
      ],
      segments: [
        { from: 'Rotterdam', to: 'Suez Canal', emissions: 'Very Low', weather: 'Variable' },
        { from: 'Suez Canal', to: 'Singapore', emissions: 'Low', weather: 'Moderate' }
      ]
    }
  ];

  const currentRoutes = mockRoutes;
  const activeRoute = selectedRoute || currentRoutes?.[0];

  const getRouteTypeIcon = (type) => {
    switch (type) {
      case 'direct': return 'ArrowRight';
      case 'weather': return 'Cloud';
      case 'emissions': return 'Leaf';
      default: return 'Route';
    }
  };

  const getRouteTypeColor = (type) => {
    switch (type) {
      case 'direct': return 'text-accent';
      case 'weather': return 'text-warning';
      case 'emissions': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getEmissionsColor = (level) => {
    switch (level) {
      case 'Very Low': return 'text-success';
      case 'Low': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'High': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getCiiRatingColor = (rating) => {
    switch (rating) {
      case 'A': return 'text-success';
      case 'B+': case 'B': return 'text-accent';
      case 'C': return 'text-warning';
      case 'D': case 'E': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-maritime-sm h-full flex flex-col">
      {/* Map Controls Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Map" size={20} className="text-accent" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Route Visualization</h2>
              <p className="text-sm text-muted-foreground">Interactive route planning and analysis</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={mapView === 'satellite' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMapView('satellite')}
            >
              Satellite
            </Button>
            <Button
              variant={mapView === 'nautical' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMapView('nautical')}
            >
              Nautical
            </Button>
          </div>
        </div>

        {/* Overlay Controls */}
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showWeatherOverlay}
              onChange={(e) => setShowWeatherOverlay(e?.target?.checked)}
              className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
            />
            <span className="text-foreground">Weather Overlay</span>
          </label>
          
          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showEmissionsOverlay}
              onChange={(e) => setShowEmissionsOverlay(e?.target?.checked)}
              className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
            />
            <span className="text-foreground">Emissions Intensity</span>
          </label>
        </div>
      </div>
      {/* Map Container */}
      <div className="flex-1 relative bg-muted/20">
        {isOptimizing ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-foreground font-medium">Optimizing routes...</p>
              <p className="text-sm text-muted-foreground">Analyzing weather patterns and emissions data</p>
            </div>
          </div>
        ) : (
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Route Visualization Map"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=51.9225,4.4792&z=4&output=embed"
            className="border-0"
          />
        )}

        {/* Route Overlay Information */}
        {!isOptimizing && (
          <div className="absolute top-4 left-4 bg-card/95 backdrop-blur-sm border border-border rounded-lg p-3 max-w-xs">
            <div className="flex items-center space-x-2 mb-2">
              <Icon 
                name={getRouteTypeIcon(activeRoute?.type)} 
                size={16} 
                className={getRouteTypeColor(activeRoute?.type)}
                strokeWidth={2} 
              />
              <span className="text-sm font-medium text-foreground">{activeRoute?.name}</span>
            </div>
            
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Distance:</span>
                <span className="text-foreground font-medium">{activeRoute?.distance}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span className="text-foreground font-medium">{activeRoute?.duration}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">CO₂ Emissions:</span>
                <span className="text-foreground font-medium">{activeRoute?.co2Emissions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">EUA Cost:</span>
                <span className="text-foreground font-medium">{activeRoute?.euaCost}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">CII Impact:</span>
                <span className={`font-medium ${getCiiRatingColor(activeRoute?.ciiImpact)}`}>
                  {activeRoute?.ciiImpact}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Weather Information */}
        {showWeatherOverlay && !isOptimizing && (
          <div className="absolute top-4 right-4 bg-card/95 backdrop-blur-sm border border-border rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Cloud" size={16} className="text-accent" strokeWidth={2} />
              <span className="text-sm font-medium text-foreground">Weather Conditions</span>
            </div>
            
            <div className="space-y-2 text-xs">
              {activeRoute?.segments?.map((segment, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-muted-foreground">{segment?.from}:</span>
                  <span className="text-foreground font-medium">{segment?.weather}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Route Selection Tabs */}
      <div className="p-4 border-t border-border">
        <div className="flex space-x-2 overflow-x-auto">
          {currentRoutes?.map((route) => (
            <button
              key={route?.id}
              onClick={() => onRouteSelect(route)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-maritime ${
                activeRoute?.id === route?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon 
                  name={getRouteTypeIcon(route?.type)} 
                  size={14} 
                  strokeWidth={2}
                />
                <span>{route?.name}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Route Comparison Summary */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Distance</div>
            <div className="text-sm font-semibold text-foreground">{activeRoute?.distance}</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Fuel Consumption</div>
            <div className="text-sm font-semibold text-foreground">{activeRoute?.fuelConsumption}</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">CO₂ Emissions</div>
            <div className="text-sm font-semibold text-foreground">{activeRoute?.co2Emissions}</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">EUA Cost</div>
            <div className="text-sm font-semibold text-foreground">{activeRoute?.euaCost}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteVisualization;